import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// Import Shared Components
import { Loader, MFAVerifyForm } from 'shared-components';
import { MFAStorage, MFAErrors, verifyMFA as genericVerifyMFA } from 'shared-components/src/utils/mfa';
// Import Local Components
import AuthForm from './AuthForm';

function MFAVerify() {
  const navigate = useNavigate();
  const [userSub, setUserSub] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    // Retrieve user_sub from sessionStorage
    const storedUserSub = MFAStorage.getUserSub();

    if (!storedUserSub) {
      // No user_sub found, redirect to login
      navigate('/auth/login');
      return;
    }

    setUserSub(storedUserSub);
  }, [navigate]);

  const getErrorMessage = (errorCode) => {
    // Map error codes to user-friendly messages (Client Portal - English only)
    const errorMessages = {
      'errors-invalid-totp-code': 'Invalid verification code. Please try again.',
      'errors-mfa-not-setup': 'MFA is not set up for your account. Please contact support.',
      'errors-mfa-session-error': 'Your session has expired. Please log in again.',
      'errors-user-not-found': 'User not found. Please log in again.',
      'errors-too-many-attempts': 'Too many failed attempts. Please try again later.',
      'errors-rate-limited': 'Too many requests. Please wait a moment and try again.',
      'errors-unauthorized': 'Authentication failed. Please log in again.',
      'errors-mfa-verification-failed': 'MFA verification failed. Please try again.',
      'errors-internal-error': 'An error occurred. Please try again later.',
      'errors-unexpected': 'An unexpected error occurred. Please try again.',
    };

    return errorMessages[errorCode] || 'An unexpected error occurred. Please try again.';
  };

  const handleMFAVerificationComplete = async (userSub, totpCode) => {
    try {
      setVerifying(true);
      setFormError(null);

      // Use the generic MFA verify utility with client-specific endpoint
      await genericVerifyMFA(
        '/api/v1/client_portal/mfa/verify',
        userSub,
        totpCode,
        // onSuccess callback
        () => {
          // Clear MFA session data
          MFAStorage.clearAll();
          // Redirect to dashboard
          window.location.pathname = '/';
        },
        // onError callback
        (error) => {
          setVerifying(false);

          // Check if it's an authentication error or validation error
          if (MFAErrors.isAuthError(error)) {
            // Redirect to login for authentication errors
            navigate('/auth/login', {
              state: {
                error: error.message,
                errorDetails: error.details
              }
            });
          } else {
            // Keep on page for validation errors (wrong TOTP code, etc.)
            const errorCode = MFAErrors.getUserFriendlyError(error);
            const errorMessage = getErrorMessage(errorCode);
            setFormError(errorMessage);
          }
        }
      );
    } catch (error) {
      // Error already handled in onError callback
      console.error('Unexpected error in MFA verification:', error);
    }
  };

  if (!userSub) {
    return (
      <AuthForm
        title="Two-Factor Authentication"
        submitButtonText="Verify"
        onSubmit={(e) => e.preventDefault()}
      >
        <Loader />
      </AuthForm>
    );
  }

  return (
    <AuthForm
      title="Two-Factor Authentication"
      error={formError}
      onSubmit={(e) => {
        e.preventDefault();
        // Form submission is handled by MFAVerifyForm onComplete
      }}
      submitButtonText="Verify"
    >
      {verifying ? (
        <Loader />
      ) : (
        <MFAVerifyForm
          userSub={userSub}
          onVerificationComplete={handleMFAVerificationComplete}
          loading={verifying}
          error={formError}
          disabled={verifying}
          autoFocus={!verifying}
          labels={{
            instruction: 'Enter the 6-digit code from your authenticator app:',
            otpLabel: 'Verification Code',
          }}
        />
      )}
    </AuthForm>
  );
};

export default MFAVerify;